'use client'

import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../../lib/utils'
import { Popover, PopoverContent, PopoverTrigger } from '../Popover'
import { Button, type ButtonProps } from '../Button'
import { UploadZone, type UploadZoneProps } from '../UploadZone'
import { FieldChange, type FieldChangeProps } from '../FieldChange'
import { type S3Response } from '../../integrations/react-s3-uploader/ReactS3Uploader'
import { UploadCsvPanel } from './UploadCsvPanel'

const popoverActionVariants = cva('', {
  variants: {
    size: {
      default: 'w-96',
      sm: 'w-80',
      lg: 'w-[28rem]',
    },
  },
  defaultVariants: {
    size: 'default',
  },
})

// Upload CSV Content Type
export type UploadCsvContent = {
  type: 'upload-csv'
  title: string
  description: string
  uploadProps: {
    accept: string
    onUploadComplete: (result: S3Response, file: File) => void
    onGetSigningUrl: (
      file: File,
      callback: (params: S3Response) => void,
    ) => void
    isLoading?: boolean
  }
  buttonProps: ButtonProps
  downloadExample?: {
    filename: string
    content: string
    label?: string
    onDownload?: () => Promise<void> | void
  }
}

// Pending Changes Content Type
export type PendingChangesContent = {
  type: 'pending-changes'
  title: string
  description?: string
  changes: Array<{
    label: string
    oldValue?: string | null
    newValue?: string | null
    onEdit?: () => void
    handleRevert?: () => void
  }>
}

// Union type for all content types
export type PopoverContentType = UploadCsvContent | PendingChangesContent

export interface PopoverActionProps
  extends VariantProps<typeof popoverActionVariants> {
  trigger: ButtonProps
  content: PopoverContentType
  className?: string
  align?: 'start' | 'center' | 'end'
  side?: 'top' | 'right' | 'bottom' | 'left'
  alignOffset?: number
  sideOffset?: number
}

const PopoverAction = React.forwardRef<HTMLDivElement, PopoverActionProps>(
  (
    {
      trigger,
      content,
      className = '',
      size = 'default',
      align = 'center',
      alignOffset = 8,
      side = 'bottom',
      sideOffset = 4,
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = React.useState(false)

    const renderContent = () => {
      switch (content.type) {
        case 'upload-csv':
          return <UploadCsvPanel content={content} />

        case 'pending-changes':
          return (
            <div className="space-y-4">
              <div className="space-y-2">
                <h3 className="font-semibold text-sm">{content.title}</h3>
                {content.description && (
                  <p className="text-sm text-muted-foreground">
                    {content.description}
                  </p>
                )}
              </div>
              <div className="space-y-3">
                {content.changes.map((change, index) => (
                  <FieldChange
                    key={index}
                    label={change.label}
                    oldValue={change.oldValue}
                    newValue={change.newValue}
                    onEdit={change.onEdit}
                    handleRevert={change.handleRevert}
                  />
                ))}
              </div>
            </div>
          )

        default:
          return null
      }
    }

    return (
      <div ref={ref} className={cn('inline-block', className)}>
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <Button {...trigger} handleClick={() => setIsOpen(!isOpen)} />
          </PopoverTrigger>
          <PopoverContent
            align={align}
            side={side}
            sideOffset={sideOffset}
            alignOffset={alignOffset}
            className={cn(popoverActionVariants({ size }), 'p-2 max-w-none')}
          >
            {renderContent()}
          </PopoverContent>
        </Popover>
      </div>
    )
  },
)

PopoverAction.displayName = 'PopoverAction'

export { PopoverAction }
